/**
 * Named desktop workspaces (SPEC §17.3; owner 2026-09-27 "moveable like Tradovate"): the pure
 * model behind the workspace tabs. One synced document, `layout/desktop` (form_factor desktop,
 * whole-document merge per §1A.3), holds every workspace; each carries its serialized dockview
 * layout, or `null` to be built from its template when opened. Everything here is pure so the
 * add / rename / duplicate / delete / reorder rules and the migration of older saved layouts are
 * unit-tested without a browser.
 */
import { sameLinkSymbols, sanitizeLinkSymbols, type LinkSymbols } from "./linkGroups.ts";
import { DEFAULT_SIDEBAR, sanitizeSidebar, type SidebarState } from "./sidebar.ts";

export const WORKSPACES_DOC_VERSION = 1;
export const MAX_WORKSPACES = 20;
export const MAX_WORKSPACE_NAME = 40;

/** A serialized dockview layout (`DockviewApi.toJSON()`); opaque here apart from a shape check. */
export type SerializedLayout = Record<string, unknown>;

export type WorkspaceEntry = {
  id: string;
  name: string;
  /** Template the workspace was built from; "Reset to template" rebuilds from it. */
  template: string;
  /** Saved layout; null = build from `template` when opened. */
  layout: SerializedLayout | null;
  /** Lock layout: panels cannot be dragged, split, resized or closed. */
  locked: boolean;
  /** Right sidebar open / width / tab (sidebar.ts); missing in older documents = closed. */
  sidebar?: SidebarState;
};

export type WorkspacesDoc = {
  v: typeof WORKSPACES_DOC_VERSION;
  activeId: string;
  workspaces: WorkspaceEntry[];
  /**
   * Link-group symbols (SPEC §12.9) of the window that saved this document: the groups are
   * window-wide, so one map for every workspace (GPT086). Missing in older documents = not saved.
   */
  links?: LinkSymbols;
};

/** Collapse whitespace and cap the length; "" when nothing usable is left. */
export function cleanWorkspaceName(name: string): string {
  return name.replace(/\s+/g, " ").trim().slice(0, MAX_WORKSPACE_NAME).trim();
}

/** `base` if unused, else `base 2`, `base 3` … (case-insensitive). */
export function uniqueWorkspaceName(doc: WorkspacesDoc | null, base: string): string {
  const taken = new Set((doc?.workspaces ?? []).map((w) => w.name.toLowerCase()));
  const b = cleanWorkspaceName(base) || "Workspace";
  if (!taken.has(b.toLowerCase())) return b;
  for (let n = 2; ; n++) {
    const candidate = `${b.slice(0, MAX_WORKSPACE_NAME - String(n).length - 1)} ${String(n)}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** Next "Workspace N" name. */
export function nextWorkspaceName(doc: WorkspacesDoc | null): string {
  const n = (doc?.workspaces.length ?? 0) + 1;
  return uniqueWorkspaceName(doc, `Workspace ${String(n)}`);
}

export function createWorkspaces(opts: { id: string; template: string; name?: string }): WorkspacesDoc {
  return {
    v: WORKSPACES_DOC_VERSION,
    activeId: opts.id,
    workspaces: [
      {
        id: opts.id,
        name: cleanWorkspaceName(opts.name ?? "") || "Workspace 1",
        template: opts.template,
        layout: null,
        locked: false,
      },
    ],
  };
}

export function activeWorkspace(doc: WorkspacesDoc): WorkspaceEntry {
  const w = doc.workspaces.find((x) => x.id === doc.activeId) ?? doc.workspaces[0];
  if (!w) throw new Error("workspaces document has no workspace");
  return w;
}

function indexOf(doc: WorkspacesDoc, id: string): number {
  return doc.workspaces.findIndex((w) => w.id === id);
}

/** Insert after `afterId` (or at the end) and make it active. No-op at the cap or on an id clash. */
function insertAfter(doc: WorkspacesDoc, afterId: string | null, entry: WorkspaceEntry): WorkspacesDoc {
  if (doc.workspaces.length >= MAX_WORKSPACES || indexOf(doc, entry.id) >= 0) return doc;
  const at = afterId === null ? -1 : indexOf(doc, afterId);
  const list = [...doc.workspaces];
  list.splice(at < 0 ? list.length : at + 1, 0, entry);
  return { ...doc, activeId: entry.id, workspaces: list };
}

/** New workspace from a template, right after the active one, and switch to it. */
export function addWorkspace(
  doc: WorkspacesDoc,
  opts: { id: string; template: string; name?: string },
): WorkspacesDoc {
  const name = opts.name !== undefined ? uniqueWorkspaceName(doc, opts.name) : nextWorkspaceName(doc);
  return insertAfter(doc, doc.activeId, {
    id: opts.id,
    name,
    template: opts.template,
    layout: null,
    locked: false,
  });
}

/** Rename; a blank name is ignored (the tab keeps its old name). */
export function renameWorkspace(doc: WorkspacesDoc, id: string, name: string): WorkspacesDoc {
  const clean = cleanWorkspaceName(name);
  const i = indexOf(doc, id);
  if (!clean || i < 0) return doc;
  return { ...doc, workspaces: doc.workspaces.map((w) => (w.id === id ? { ...w, name: clean } : w)) };
}

/** Copy (layout included, deep) right after the source, named "<name> copy", and switch to it. */
export function duplicateWorkspace(doc: WorkspacesDoc, id: string, newId: string): WorkspacesDoc {
  const src = doc.workspaces.find((w) => w.id === id);
  if (!src) return doc;
  return insertAfter(doc, id, {
    id: newId,
    name: uniqueWorkspaceName(doc, `${src.name.slice(0, MAX_WORKSPACE_NAME - 5)} copy`),
    template: src.template,
    layout: src.layout === null ? null : structuredClone(src.layout),
    locked: false,
    ...(src.sidebar ? { sidebar: { ...src.sidebar } } : {}),
  });
}

/**
 * Delete. The last workspace cannot be deleted. Deleting the active one activates its right
 * neighbour (or the left one when it was last).
 */
export function removeWorkspace(doc: WorkspacesDoc, id: string): WorkspacesDoc {
  const i = indexOf(doc, id);
  if (i < 0 || doc.workspaces.length <= 1) return doc;
  const list = doc.workspaces.filter((w) => w.id !== id);
  let activeId = doc.activeId;
  if (activeId === id) activeId = (list[i] ?? list[i - 1] ?? list[0])?.id ?? activeId;
  return { ...doc, activeId, workspaces: list };
}

/** Move a workspace tab to `toIndex` (clamped). */
export function moveWorkspace(doc: WorkspacesDoc, id: string, toIndex: number): WorkspacesDoc {
  const i = indexOf(doc, id);
  if (i < 0) return doc;
  const list = [...doc.workspaces];
  const [w] = list.splice(i, 1);
  if (!w) return doc;
  const to = Math.max(0, Math.min(list.length, Math.trunc(toIndex)));
  if (to === i) return doc;
  list.splice(to, 0, w);
  return { ...doc, workspaces: list };
}

export function setActiveWorkspace(doc: WorkspacesDoc, id: string): WorkspacesDoc {
  return indexOf(doc, id) < 0 || doc.activeId === id ? doc : { ...doc, activeId: id };
}

export function setWorkspaceLayout(
  doc: WorkspacesDoc,
  id: string,
  layout: SerializedLayout | null,
): WorkspacesDoc {
  if (indexOf(doc, id) < 0) return doc;
  return { ...doc, workspaces: doc.workspaces.map((w) => (w.id === id ? { ...w, layout } : w)) };
}

/** Remember the window's link-group symbols (no-op when unchanged; empty = none saved). */
export function setLinkSymbols(doc: WorkspacesDoc, links: LinkSymbols): WorkspacesDoc {
  if (sameLinkSymbols(doc.links, links)) return doc;
  const next: WorkspacesDoc = { ...doc };
  if (Object.keys(links).length > 0) next.links = { ...links };
  else delete next.links;
  return next;
}

/** Remember the right sidebar state for a workspace (no-op when unchanged). */
export function setWorkspaceSidebar(doc: WorkspacesDoc, id: string, sidebar: SidebarState): WorkspacesDoc {
  const w = doc.workspaces.find((x) => x.id === id);
  if (!w) return doc;
  const cur = workspaceSidebar(w);
  if (cur.open === sidebar.open && cur.width === sidebar.width && cur.tab === sidebar.tab) return doc;
  return { ...doc, workspaces: doc.workspaces.map((x) => (x.id === id ? { ...x, sidebar } : x)) };
}

export function workspaceSidebar(w: WorkspaceEntry): SidebarState {
  return w.sidebar ?? DEFAULT_SIDEBAR;
}

export function setWorkspaceLocked(doc: WorkspacesDoc, id: string, locked: boolean): WorkspacesDoc {
  if (indexOf(doc, id) < 0) return doc;
  return { ...doc, workspaces: doc.workspaces.map((w) => (w.id === id ? { ...w, locked } : w)) };
}

/** Throw the saved layout away so the workspace is rebuilt from `template` (default: its own). */
export function resetWorkspace(doc: WorkspacesDoc, id: string, template?: string): WorkspacesDoc {
  if (indexOf(doc, id) < 0) return doc;
  return {
    ...doc,
    workspaces: doc.workspaces.map((w) =>
      w.id === id ? { ...w, layout: null, template: template ?? w.template } : w,
    ),
  };
}

// ---------------------------------------------------------------------------------------------
// Validation and migration

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Looks like a dockview `toJSON()` (a grid root and a panels map). */
export function isSerializedLayout(v: unknown): v is SerializedLayout {
  return isRecord(v) && isRecord(v["grid"]) && isRecord(v["grid"]["root"]) && isRecord(v["panels"]);
}

/**
 * Validate a stored or synced document. Bad entries are dropped (a bad layout becomes `null`, i.e.
 * rebuilt from the template) instead of failing the whole document; ids are de-duplicated and the
 * active id repaired. Null when nothing usable is left.
 */
export function sanitizeWorkspaces(raw: unknown, fallbackTemplate: string): WorkspacesDoc | null {
  if (!isRecord(raw) || !Array.isArray(raw["workspaces"])) return null;
  const seen = new Set<string>();
  const list: WorkspaceEntry[] = [];
  for (const w of raw["workspaces"] as unknown[]) {
    if (!isRecord(w) || typeof w["id"] !== "string" || w["id"] === "" || seen.has(w["id"])) continue;
    if (list.length >= MAX_WORKSPACES) break;
    seen.add(w["id"]);
    const name = typeof w["name"] === "string" ? cleanWorkspaceName(w["name"]) : "";
    list.push({
      id: w["id"],
      name: name || `Workspace ${String(list.length + 1)}`,
      template: typeof w["template"] === "string" && w["template"] ? w["template"] : fallbackTemplate,
      layout: isSerializedLayout(w["layout"]) ? w["layout"] : null,
      locked: w["locked"] === true,
      ...(w["sidebar"] !== undefined ? { sidebar: sanitizeSidebar(w["sidebar"]) } : {}),
    });
  }
  const first = list[0];
  if (!first) return null;
  const activeId =
    typeof raw["activeId"] === "string" && seen.has(raw["activeId"]) ? raw["activeId"] : first.id;
  const links = sanitizeLinkSymbols(raw["links"]);
  return { v: WORKSPACES_DOC_VERSION, activeId, workspaces: list, ...(links ? { links } : {}) };
}

/**
 * Choose the document at start-up: the stored workspaces document when valid; otherwise the
 * single-layout save from before workspaces (`helm:workspace:desktop`, a bare dockview layout)
 * becomes "Workspace 1" unchanged, so existing users keep their layout; otherwise a fresh one.
 */
export function loadWorkspaces(opts: { stored: unknown; legacy: unknown; template: string; newId: string }): {
  doc: WorkspacesDoc;
  source: "stored" | "legacy" | "fresh";
} {
  const stored = sanitizeWorkspaces(opts.stored, opts.template);
  if (stored) return { doc: stored, source: "stored" };
  if (isSerializedLayout(opts.legacy)) {
    const doc = createWorkspaces({ id: opts.newId, template: opts.template });
    return { doc: setWorkspaceLayout(doc, opts.newId, opts.legacy), source: "legacy" };
  }
  return { doc: createWorkspaces({ id: opts.newId, template: opts.template }), source: "fresh" };
}

/** Parse JSON that may be missing or corrupt; undefined on any failure. */
export function parseJsonOrUndefined(raw: string | null): unknown {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Pop-out windows cannot be reopened without a click (browsers block `window.open` outside a user
 * gesture), and a layout synced from another device must not open windows here. So a saved
 * layout's pop-out groups come back as floating panels, cascaded from the top-left; one click
 * pops them out again.
 */
export function popoutsToFloating(layout: SerializedLayout): SerializedLayout {
  const popouts = layout["popoutGroups"];
  if (!Array.isArray(popouts) || popouts.length === 0) return layout;
  const floating = Array.isArray(layout["floatingGroups"])
    ? [...(layout["floatingGroups"] as unknown[])]
    : [];
  let n = floating.length;
  for (const p of popouts as unknown[]) {
    if (!isRecord(p)) continue;
    const pos = isRecord(p["position"]) ? p["position"] : {};
    const width = typeof pos["width"] === "number" ? Math.min(Math.max(pos["width"], 280), 900) : 480;
    const height = typeof pos["height"] === "number" ? Math.min(Math.max(pos["height"], 200), 700) : 360;
    const position = { left: 60 + 28 * n, top: 60 + 28 * n, width, height };
    if (isRecord(p["data"])) floating.push({ data: p["data"], position });
    else if (isRecord(p["grid"])) floating.push({ grid: p["grid"], position });
    else continue;
    n++;
  }
  const out: SerializedLayout = { ...layout, floatingGroups: floating };
  delete out["popoutGroups"];
  return out;
}

/**
 * Module kinds that are no longer dock modules. Settings opens as a window-wide dialog (owner
 * 2026-10-04), so a saved layout's Settings tab is dropped when the layout is read.
 */
export const RETIRED_PANEL_KINDS: readonly string[] = ["settings"];

/**
 * Migration on read: remove every panel whose `params.kind` is in `kinds` from a serialized
 * layout, and nothing else. Its tab leaves its group (the group's active tab moves to a remaining
 * one); a group left empty leaves the grid and its size goes to the neighbour it was split from,
 * so the other modules keep their place and size; an emptied floating / pop-out group is dropped.
 * The same object comes back when there is nothing to remove (no write-back).
 */
export function dropPanelsOfKind(layout: SerializedLayout, kinds: readonly string[]): SerializedLayout {
  const panels = isRecord(layout["panels"]) ? layout["panels"] : null;
  if (panels === null || kinds.length === 0) return layout;
  const drop = new Set<string>();
  for (const [id, p] of Object.entries(panels)) {
    const params = isRecord(p) ? p["params"] : undefined;
    if (isRecord(params) && typeof params["kind"] === "string" && kinds.includes(params["kind"]))
      drop.add(id);
  }
  return dropPanels(layout, drop);
}

/** The `params` of every panel of a serialized layout, by panel id. */
function panelParams(layout: SerializedLayout): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  const panels = isRecord(layout["panels"]) ? layout["panels"] : {};
  for (const [id, p] of Object.entries(panels)) {
    const params = isRecord(p) ? p["params"] : undefined;
    if (isRecord(params)) out.set(id, params);
  }
  return out;
}

/** Every group's view ids (grid leaves, floating / pop-out windows, edge docks). */
function groupViews(layout: SerializedLayout): string[][] {
  const out: string[][] = [];
  const group = (g: unknown): void => {
    if (isRecord(g) && Array.isArray(g["views"]))
      out.push((g["views"] as unknown[]).filter((v): v is string => typeof v === "string"));
  };
  const node = (n: unknown): void => {
    if (!isRecord(n)) return;
    if (n["type"] === "leaf") group(n["data"]);
    else if (n["type"] === "branch" && Array.isArray(n["data"]))
      for (const c of n["data"] as unknown[]) node(c);
  };
  const grid = layout["grid"];
  if (isRecord(grid)) node(grid["root"]);
  for (const key of ["floatingGroups", "popoutGroups"] as const) {
    const list = layout[key];
    if (!Array.isArray(list)) continue;
    for (const w of list as unknown[]) {
      if (!isRecord(w)) continue;
      group(w["data"]);
      if (isRecord(w["grid"])) node(w["grid"]["root"]);
    }
  }
  const edges = layout["edgeGroups"];
  if (isRecord(edges)) for (const e of Object.values(edges)) if (isRecord(e)) group(e["group"]);
  return out;
}

/** Same table view: Accounts with the same "Main account only / All accounts" mode. */
function tableViewKey(params: Record<string, unknown>): string | null {
  if (params["kind"] !== "accounts") return null;
  return `accounts:${params["accountsView"] === "main" ? "main" : "all"}`;
}

/**
 * Migration on read (owner 2026-10-04): a dock showing the same Accounts table twice in one group
 * keeps the first tab; the repeats leave (`dropPanels`). Accounts tables in other groups, or with
 * the other view, stay. The same object comes back when nothing repeats.
 */
export function dedupeAccountsTabs(layout: SerializedLayout): SerializedLayout {
  const params = panelParams(layout);
  const drop = new Set<string>();
  for (const views of groupViews(layout)) {
    const seen = new Set<string>();
    for (const id of views) {
      const p = params.get(id);
      const key = p ? tableViewKey(p) : null;
      if (key === null) continue;
      if (seen.has(key)) drop.add(id);
      else seen.add(key);
    }
  }
  return dropPanels(layout, drop);
}

/**
 * Every migration a saved or synced layout takes before dockview loads it: retired modules
 * (Settings) leave, a repeated Accounts tab leaves, and pop-outs come back floating. Module params
 * (symbol, link, account filters…) are untouched. The same object comes back when none applies.
 */
export function migrateLayoutOnRead(layout: SerializedLayout): SerializedLayout {
  return popoutsToFloating(dedupeAccountsTabs(dropPanelsOfKind(layout, RETIRED_PANEL_KINDS)));
}

/**
 * Remove the panels `drop` names from a serialized layout, and nothing else (see
 * `dropPanelsOfKind`). The same object comes back when `drop` names none of them.
 */
export function dropPanels(layout: SerializedLayout, drop: ReadonlySet<string>): SerializedLayout {
  const panels = isRecord(layout["panels"]) ? layout["panels"] : null;
  if (panels === null || drop.size === 0 || !Object.keys(panels).some((id) => drop.has(id))) return layout;
  const removedGroups = new Set<string>();

  /** A group's state without the dropped views; null when it had views and none is left. */
  const groupState = (g: Record<string, unknown>): Record<string, unknown> | null => {
    const views = Array.isArray(g["views"]) ? (g["views"] as unknown[]) : [];
    const kept = views.filter((v) => !(typeof v === "string" && drop.has(v)));
    if (kept.length === views.length) return g;
    if (kept.length === 0) {
      if (typeof g["id"] === "string") removedGroups.add(g["id"]);
      return null;
    }
    const active = g["activeView"];
    const out: Record<string, unknown> = { ...g, views: kept };
    if (typeof active === "string" && drop.has(active)) out["activeView"] = kept[0];
    return out;
  };

  /** A grid node without the dropped views; null when nothing is left in it. */
  const node = (n: unknown): unknown => {
    if (!isRecord(n)) return n;
    if (n["type"] === "leaf") {
      if (!isRecord(n["data"])) return n;
      const data = groupState(n["data"]);
      if (data === null) return null;
      return data === n["data"] ? n : { ...n, data };
    }
    if (n["type"] !== "branch" || !Array.isArray(n["data"])) return n;
    const children = n["data"] as unknown[];
    const out: unknown[] = [];
    let changed = false;
    // Sizes freed by removed children go to the sibling before them (else the one after).
    let carry = 0;
    for (const c of children) {
      const next = node(c);
      if (next !== c) changed = true;
      const size = isRecord(c) && typeof c["size"] === "number" ? c["size"] : 0;
      if (next === null) {
        const prev = out.at(-1);
        if (isRecord(prev) && typeof prev["size"] === "number")
          out[out.length - 1] = { ...prev, size: prev["size"] + size };
        else carry += size;
        continue;
      }
      if (carry > 0 && isRecord(next) && typeof next["size"] === "number") {
        out.push({ ...next, size: next["size"] + carry });
        carry = 0;
      } else out.push(next);
    }
    if (!changed) return n;
    if (out.length === 0) return null;
    return { ...n, data: out };
  };

  /** A floating / pop-out entry (one group in `data`, or a nested grid in `grid`); null if emptied. */
  const windowEntry = (w: unknown): unknown => {
    if (!isRecord(w)) return w;
    if (isRecord(w["data"])) {
      const data = groupState(w["data"]);
      if (data === null) return null;
      return data === w["data"] ? w : { ...w, data };
    }
    if (isRecord(w["grid"]) && isRecord(w["grid"]["root"])) {
      const root = node(w["grid"]["root"]);
      if (root === null) return null;
      return root === w["grid"]["root"] ? w : { ...w, grid: { ...w["grid"], root } };
    }
    return w;
  };

  const out: SerializedLayout = { ...layout };
  const keptPanels: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(panels)) if (!drop.has(id)) keptPanels[id] = p;
  out["panels"] = keptPanels;

  const grid = layout["grid"];
  if (isRecord(grid) && isRecord(grid["root"])) {
    const root = node(grid["root"]);
    if (root !== grid["root"]) {
      // dockview needs a branch root; a layout of nothing but dropped panels becomes empty.
      const nextGrid: Record<string, unknown> = {
        ...grid,
        root: root ?? { ...grid["root"], data: [] },
      };
      // A maximized node is addressed by its grid path, which may have moved.
      delete nextGrid["maximizedNode"];
      out["grid"] = nextGrid;
    }
  }
  for (const key of ["floatingGroups", "popoutGroups"] as const) {
    const list = layout[key];
    if (!Array.isArray(list)) continue;
    out[key] = (list as unknown[]).map(windowEntry).filter((w) => w !== null);
  }
  const edges = layout["edgeGroups"];
  if (isRecord(edges)) {
    // Edge groups are fixed docks: they stay, possibly empty.
    const nextEdges: Record<string, unknown> = {};
    for (const [pos, e] of Object.entries(edges)) {
      if (!isRecord(e) || !isRecord(e["group"])) {
        nextEdges[pos] = e;
        continue;
      }
      const g = e["group"];
      const views = Array.isArray(g["views"]) ? (g["views"] as unknown[]) : [];
      const kept = views.filter((v) => !(typeof v === "string" && drop.has(v)));
      const active = g["activeView"];
      const group: Record<string, unknown> = { ...g, views: kept };
      if (typeof active === "string" && drop.has(active)) {
        if (kept.length > 0) group["activeView"] = kept[0];
        else delete group["activeView"];
      }
      nextEdges[pos] = { ...e, group };
    }
    out["edgeGroups"] = nextEdges;
  }
  if (typeof layout["activeGroup"] === "string" && removedGroups.has(layout["activeGroup"]))
    delete out["activeGroup"];
  return out;
}

/** Panel ids in a serialized layout (for tests and "does this workspace contain X"). */
export function layoutPanelIds(layout: SerializedLayout): string[] {
  return isRecord(layout["panels"]) ? Object.keys(layout["panels"]) : [];
}
